import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Paper from '@mui/material/Paper';
import CircularProgress from '@mui/material/CircularProgress';
import { ClockCircleOutlined, PercentageOutlined, VideoCameraOutlined, TrophyOutlined } from '@ant-design/icons';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { sendReport } from 'services/report/sendReport';

// Animation variants for cards
const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.2, duration: 0.5 }
  })
};

const ViewStudentPoseData = () => {
  const { state } = useLocation();
  const sessionData = state?.sessionData || {
    captureDuration: 0,
    averageAccuracy: 0,
    frameCount: 0,
    goodPostureTime: 0,
    studentId: undefined
  };

  console.log('🔥🔥🔥🔥', sessionData);

  // Validate sessionData
  const isValidSessionData =
    sessionData.studentId &&
    Number.isFinite(sessionData.captureDuration) &&
    Number.isFinite(sessionData.averageAccuracy) &&
    Number.isFinite(sessionData.frameCount) &&
    Number.isFinite(sessionData.goodPostureTime);

  // Card data for dynamic rendering
  const metrics = [
    {
      title: 'Capture Duration',
      value: `${sessionData.captureDuration} s`,
      icon: <ClockCircleOutlined style={{ fontSize: '28px', color: '#3f8600' }} />,
      bgColor: 'bg-gradient-to-r from-green-100 to-green-200',
      ariaLabel: `Capture duration: ${sessionData.captureDuration} seconds`
    },
    {
      title: 'Average Accuracy',
      value: `${sessionData.averageAccuracy}%`,
      icon: <PercentageOutlined style={{ fontSize: '28px', color: '#cf1322' }} />,
      bgColor: 'bg-gradient-to-r from-red-100 to-red-200',
      ariaLabel: `Average accuracy: ${sessionData.averageAccuracy}%`
    },
    {
      title: 'Frames Captured',
      value: sessionData.frameCount,
      icon: <VideoCameraOutlined style={{ fontSize: '28px', color: '#1890ff' }} />,
      bgColor: 'bg-gradient-to-r from-blue-100 to-blue-200',
      ariaLabel: `Frames captured: ${sessionData.frameCount}`
    },
    {
      title: 'Good Posture Time',
      value: `${sessionData.goodPostureTime} s`,
      icon: <TrophyOutlined style={{ fontSize: '28px', color: '#ffa000' }} />,
      bgColor: 'bg-gradient-to-r from-amber-100 to-amber-200',
      ariaLabel: `Good posture time: ${sessionData.goodPostureTime} seconds`
    }
  ];

  return (
    <Box
      sx={{
        maxWidth: 1400,
        mx: 'auto',
        p: { xs: 2, sm: 3, md: 4 },
        borderRadius: 4,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center'
      }}
      className="min-h-screen"
    >
      <Typography
        variant="h3"
        component="h1"
        gutterBottom
        sx={{
          fontWeight: 'bold',
          color: 'primary.dark',
          textAlign: 'center',
          mb: 5,
          fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' }
        }}
      >
        Pose Tracking Summary
      </Typography>

      <Paper
        elevation={6}
        sx={{
          p: { xs: 2, sm: 3, md: 4 },
          borderRadius: 4,
          bgcolor: 'background.paper',
          width: '100%',
          maxWidth: 1200
        }}
      >
        <Grid container spacing={{ xs: 2, sm: 3, md: 4 }}>
          {metrics.map((metric, index) => (
            <Grid item xs={12} sm={6} md={6} key={metric.title}>
              <motion.div custom={index} initial="hidden" animate="visible" variants={cardVariants}>
                <Card
                  sx={{
                    borderRadius: 3,
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-5px)'
                    }
                  }}
                  className={metric.bgColor}
                  aria-label={metric.ariaLabel}
                >
                  <CardContent
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      p: 3,
                      position: 'relative'
                    }}
                  >
                    {metric.title === 'Average Accuracy' && Number.isFinite(parseFloat(sessionData.averageAccuracy)) && (
                      <CircularProgress
                        variant="determinate"
                        value={Math.min(parseFloat(sessionData.averageAccuracy), 100)}
                        size={60}
                        thickness={5}
                        sx={{
                          color: sessionData.averageAccuracy > 90 ? 'success.main' : 'error.main',
                          position: 'absolute',
                          right: 16,
                          top: 16
                        }}
                      />
                    )}
                    {metric.icon}
                    <Box>
                      <Typography variant="subtitle2" sx={{ color: 'text.secondary', fontSize: '0.9rem', fontWeight: 500 }}>
                        {metric.title}
                      </Typography>
                      <Typography
                        variant="h5"
                        sx={{
                          fontWeight: 'bold',
                          color: 'text.primary',
                          fontSize: { xs: '1.25rem', sm: '1.5rem' }
                        }}
                      >
                        {metric.value}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </Paper>
    </Box>
  );
};

export default ViewStudentPoseData;
